import React, { useState } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { FaSearchMinus, FaSearchPlus } from "react-icons/fa";
import { MdOutlineRotateLeft, MdOutlineRotateRight } from "react-icons/md";
import "./CSS/DocumentAgentPDF.css";

interface DocumentAgentPDFProps {
  pdfUrls: string[];
  pdfNames?: string[];
  documentName?: string;
  className?: string;
  style?: React.CSSProperties;
  showControls?: boolean;
  showTabs?: boolean;
}

const DocumentAgentPDF: React.FC<DocumentAgentPDFProps> = ({
  pdfUrls,
  pdfNames = [],
  documentName = "PDF Document",
  className = "",
  style = {},
  showControls = true,
  showTabs = true,
}) => {
  // PDF display states
  const [pdfRotation, setPdfRotation] = useState<number>(0);
  const [pdfScale, setPdfScale] = useState<number>(1);
  const [activePdfTab, setActivePdfTab] = useState<string>("pdf-0");

  // Functions to rotate left (counter-clockwise) or right (clockwise)
  const rotateLeft = () => {
    setPdfRotation(prev => (prev - 90) % 360);
  };

  const rotateRight = () => {
    setPdfRotation(prev => (prev + 90) % 360);
  };

  // Zoom in and out
  const zoomIn = () => {
    setPdfScale(prev => Math.min(prev + 0.2, 5)); // Limit max zoom
  };

  const zoomOut = () => {
    setPdfScale(prev => Math.max(prev - 0.2, 0.3)); // Limit min zoom
  };

  // Reset PDF view
  const resetView = () => {
    setPdfRotation(0);
    setPdfScale(1);
  };

  // If no PDFs provided, show placeholder
  if (!pdfUrls || pdfUrls.length === 0) {
    return (
      <div
        className={`pdf-viewer-placeholder ${className}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          color: "#6c757d",
          ...style,
        }}
      >
        <div className="text-center">
          <p>No PDF available</p>
        </div>
      </div>
    );
  }

  // Single PDF view
  const renderSinglePDF = (pdfUrl: string, index: number = 0) => (
    <div
      className="pdf-display-content"
      style={{ position: "relative", height: "100%", ...style }}
    >
      {/* Floating control buttons */}
      {showControls && (
        <div className="pdf-viewer-controls">
          <button onClick={rotateLeft} title="Rotate 90° Left">
            <MdOutlineRotateLeft size={16} />
          </button>
          <button onClick={rotateRight} title="Rotate 90° Right">
            <MdOutlineRotateRight size={16} />
          </button>
          <button onClick={zoomOut} title="Zoom Out">
            <FaSearchMinus size={14} />
          </button>
          <button onClick={zoomIn} title="Zoom In">
            <FaSearchPlus size={14} />
          </button>
          <button
            onClick={resetView}
            title="Reset View"
            style={{ fontSize: "12px" }}
          >
            Reset
          </button>
        </div>
      )}

      <div
        style={{
          transform: `rotate(${pdfRotation}deg) scale(${pdfScale})`,
          transformOrigin: "center center",
          width: "100%",
          height: "100%",
        }}
      >
        <embed
          src={`${pdfUrl}#toolbar=0`}
          type="application/pdf"
          style={{ width: "100%", height: "100vh" }}
        />
      </div>
    </div>
  );

  // Multi-PDF tabbed view
  const renderTabbedPDFs = () => (
    <div
      className="pdf-display-content"
      style={{ position: "relative", ...style }}
    >
      {/* Floating control buttons */}
      {showControls && (
        <div className="pdf-viewer-controls" style={{ top: "60px" }}>
          <button onClick={rotateLeft} title="Rotate 90° Left">
            <MdOutlineRotateLeft size={16} />
          </button>
          <button onClick={rotateRight} title="Rotate 90° Right">
            <MdOutlineRotateRight size={16} />
          </button>
          <button onClick={zoomOut} title="Zoom Out">
            <FaSearchMinus size={14} />
          </button>
          <button onClick={zoomIn} title="Zoom In">
            <FaSearchPlus size={14} />
          </button>
          <button
            onClick={resetView}
            title="Reset View"
            style={{ fontSize: "12px" }}
          >
            Reset
          </button>
        </div>
      )}

      <Tabs
        activeKey={activePdfTab}
        onSelect={k => setActivePdfTab(k || "pdf-0")}
        id="pdf-tabs"
        className="document-pdf-tabs document-pdf-nav-tabs"
        variant="scrollable"
      >
        {pdfUrls.map((pdfUrl, index) => {
          const tabName = pdfNames[index] || `PDF ${index + 1}`;
          return (
            <Tab
              key={`pdf-${index}`}
              eventKey={`pdf-${index}`}
              title={tabName}
              tabClassName="document-pdf-tab-pane"
            >
              <div
                className="document-pdf-tab-container"
                style={{
                  transform: `rotate(${pdfRotation}deg) scale(${pdfScale})`,
                  transformOrigin: "center center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <embed
                  src={`${pdfUrl}#toolbar=0`}
                  type="application/pdf"
                  style={{ width: "100%", height: "100vh" }}
                />
              </div>
            </Tab>
          );
        })}
      </Tabs>
    </div>
  );

  // Decide which view to render
  if (pdfUrls.length === 1 || !showTabs) {
    return <div className={className}>{renderSinglePDF(pdfUrls[0], 0)}</div>;
  }

  return <div className={className}>{renderTabbedPDFs()}</div>;
};

export default DocumentAgentPDF;
